import React, {
    useContext,
    useCallback,
    useEffect,
    useState,
    Suspense,
} from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { ThemeProvider, Grid, GridColumn, Page } from '@fairygodboss/ui';
import { Header } from './Header';
import { InfoV2 } from './infoV2';
import {
    JobContext,
    JobSearchContext,
    PageContext,
} from '../../../../contexts/ContextCreator';
import { convincerPropType, companyPropType, refPropType } from './constants';
import { jobDetailsPropType } from '../../../Includes/JobProps';
import { buildJobViewMetrics } from '../../JobsSearch/utils';
import JobsBackLink from './JobsBackLink';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';

export const determineJobLikedStatus = (hash, content = []) => {
    if (!content || content.length === 0) return false;
    const currentJob = content.find((job) => job.hash === hash);
    return currentJob ? currentJob.userLiked : false;
};

const GoogleOneTap = React.lazy(() =>
    import('../../../../components/Login/GoogleOneTap/GoogleOneTap')
);

const About = loadable(() => import('./About'), { ssr: false });
const Body = loadable(() => import('./Body'), { ssr: false });
const ApplyBottom = loadable(() => import('./ApplyBottom'), { ssr: false });
const Benefits = loadable(() => import('./Benefits'), { ssr: false });
const CTA = loadable(() => import('./CTA'), { ssr: false });

export const SingleJobTemplateV2 = ({
    convincers,
    company,
    jobDetails,
    handleCompanyApplyClick,
    topApplyButtonGroupRef,
    bottomApplyButtonGroupRef,
    handleQuickApplyClick,
    userLiked,
    onJobSaveClick,
    hasCookie,
    cookieBannerHeight,
    jobSearchVariant,
    searchQueryId,
    hasCTA,
    isJobsFindPage,
}) => {
    const {
        title,
        locationName,
        jobRequestId,
        description,
        htmlDescription,
        about,
        verifiedBenefits,
        slug,
        isSponsored,
        videoUrl,
    } = jobDetails;

    const {
        companyId,
        companyName,
        rating,
        industry,
        employerInfo,
        companyUrl,
        logo,
    } = company;

    const { companySize, hq } = employerInfo;
    const [metricLogged, setMetricLogged] = useState(null);

    const [metricSearchQuery, setMetricSearchQuery] = useState(null);

    const [isMounted, setIsMounted] = useState(false);

    const jobContext = useContext(JobContext);
    const { isLoggedIn, pageProps } = useContext(PageContext);
    const { fullSearchQueryId } = useContext(JobSearchContext);
    let toggleJobLike = null;
    let content = null;
    if (!onJobSaveClick && jobContext) {
        const { toggleJobLike: _toggleJobLike, content: _content } = jobContext;
        toggleJobLike = _toggleJobLike;
        content = _content;
    }
    useEffect(() => {
        setIsMounted(true);
    }, []);

    const showGoogleoneTapLogin = () => {
        return (
            typeof window !== 'undefined' &&
            window?.document.referrer.includes('google.com') &&
            !isLoggedIn()
        );
    };

    const handleSaveClick = useCallback(() => {
        if (onJobSaveClick) {
            onJobSaveClick({ userLiked, hash: slug, posistion: 'top' });
            return;
        }
        toggleJobLike(slug);
    }, [toggleJobLike, slug, onJobSaveClick, userLiked]);

    const handleSaveClickBottom = useCallback(() => {
        if (onJobSaveClick) {
            onJobSaveClick({ userLiked, hash: slug, posistion: 'bottom' });
            return;
        }
        toggleJobLike(slug);
    }, [toggleJobLike, slug, onJobSaveClick, userLiked]);

    const isSaved = onJobSaveClick
        ? userLiked
        : determineJobLikedStatus(slug, content);

    useEffect(() => {
        if (
            metricLogged &&
            (metricLogged === fullSearchQueryId ||
                metricSearchQuery !== searchQueryId)
        ) {
            return;
        }
        if (metricLogged !== fullSearchQueryId && metricLogged !== null) {
            setMetricLogged(null);
            return;
        }
        const { applyUrl, category } = jobDetails;
        const jobViewMetric = buildJobViewMetrics({
            originalApplyUrl: applyUrl,
            category,
            company: { id: company?.companyId, name: company?.companyName },
            hash: slug,
            title,
            location: locationName,
            jobSearchQueryID: searchQueryId,
            miscEventType: 'single-job-page-view',
            impression: 1,
        });
        jobViewMetric.snowPlow = {
            component: 'card',
            cardType: 'job',
            position: 'main',
            pageType: 'collection',
            pageSection: 'search',
            searchId: `${fullSearchQueryId}`,
            companyId: company?.companyId,
            isSponsored: !!isSponsored,
            cardEntityId: slug,
        };
        EmitMetric(jobViewMetric);
        setMetricLogged(fullSearchQueryId);
        setMetricSearchQuery(searchQueryId);
    }, [
        jobDetails,
        company,
        locationName,
        slug,
        title,
        metricLogged,
        searchQueryId,
        fullSearchQueryId,
    ]);

    const buildAboutVideoImpressionMetrics = ({
        jobSlug,
        searchId,
        sponsored,
        jobCompanyId,
        jobCompanyName,
    }) => ({
        searchId,
        videoAboutCard: true,
        component: 'insert',
        insertType: 'media',
        position: 'bottom',
        pageType: 'collection',
        pageSection: 'search',
        cardType: 'job',
        isSponsored: sponsored,
        cardEntityId: jobSlug,
        companyId: jobCompanyId,
        cardPosition: 'main',
        mediaType: 'video',
        companyName: jobCompanyName,
    });

    const buildAboutVideoPlayMetrics = ({
        sponsored,
        jobSlug,
        jobCompanyId,
        jobCompanyName,
    }) => ({
        pageType: 'collection',
        pageSection: 'search',
        cardType: 'job',
        isSponsored: sponsored,
        cardEntityId: jobSlug,
        companyId: jobCompanyId,
        companyName: jobCompanyName,
    });

    return (
        <ThemeProvider>
            <Page>
                {isMounted && showGoogleoneTapLogin() && (
                    <Suspense fallback={null}>
                        <GoogleOneTap gclientId={pageProps.googleClientId} />
                    </Suspense>
                )}
                <Grid
                    layout="fixed"
                    background="#fff"
                    justifyContent="center"
                    marginTop="0px"
                    paddingBottom={{
                        _: hasCookie ? '16px' : `${cookieBannerHeight + 16}px`,
                        sm: hasCookie ? '48px' : `${cookieBannerHeight}px`,
                        md: jobSearchVariant ? '24px' : '48px',
                    }}
                >
                    <GridColumn size={{ md: jobSearchVariant ? 12 : 8 }}>
                        {isJobsFindPage ? null : (
                            <JobsBackLink job={jobDetails} company={company} />
                        )}
                        <Header
                            jobSearchVariant={jobSearchVariant}
                            logo={logo}
                            companyUrl={companyUrl}
                            title={title}
                            companyName={companyName}
                            industry={industry}
                            rating={rating}
                            locationName={locationName}
                            hqLocation={hq}
                            companySize={companySize}
                            handleSaveClick={handleSaveClick}
                            isSaved={isSaved}
                            handleCompanyApplyClick={handleCompanyApplyClick}
                            handleQuickApplyClick={handleQuickApplyClick}
                            showDirectApply={jobDetails.showDirectApply}
                            topApplyButtonGroupRef={topApplyButtonGroupRef}
                        />
                        <span>
                            <InfoV2
                                jobSearchVariant={jobSearchVariant}
                                companyName={companyName}
                                companyUrl={companyUrl}
                                convincers={convincers}
                                hideForMobile={!isLoggedIn()}
                            />
                        </span>
                        <Body
                            jobSearchVariant={jobSearchVariant}
                            jobRequestId={jobRequestId}
                            description={htmlDescription}
                            fallbackDescription={description}
                        />
                        {hasCTA && !jobSearchVariant ? <CTA /> : null}
                        <Benefits
                            jobSearchVariant={jobSearchVariant}
                            verifiedBenefits={verifiedBenefits}
                            companyName={companyName}
                        />
                        <About
                            jobSearchVariant={jobSearchVariant}
                            logo={logo}
                            about={about}
                            videoUrl={videoUrl}
                            industry={industry !== '' ? industry : undefined}
                            companyName={companyName}
                            companyUrl={companyUrl}
                            searchId={fullSearchQueryId}
                            impressionMetric={buildAboutVideoImpressionMetrics({
                                sponsored: !!isSponsored,
                                jobSlug: slug,
                                jobCompanyId: companyId,
                                jobCompanyName: companyName,
                                searchId: `${fullSearchQueryId}`,
                            })}
                            videoPlayMetric={buildAboutVideoPlayMetrics({
                                sponsored: !!isSponsored,
                                jobSlug: slug,
                                jobCompanyId: companyId,
                                jobCompanyName: companyName,
                            })}
                        />

                        <span>
                            <InfoV2
                                jobSearchVariant={jobSearchVariant}
                                companyName={companyName}
                                companyUrl={companyUrl}
                                convincers={convincers}
                            />
                        </span>

                        <ApplyBottom
                            isSaved={isSaved}
                            handleSaveClick={handleSaveClickBottom}
                            handleCompanyApplyClick={handleCompanyApplyClick}
                            ref={bottomApplyButtonGroupRef}
                            handleQuickApplyClick={handleQuickApplyClick}
                            showDirectApply={jobDetails.showDirectApply}
                        />
                    </GridColumn>
                </Grid>
            </Page>
        </ThemeProvider>
    );
};

SingleJobTemplateV2.propTypes = {
    convincers: PropTypes.arrayOf(convincerPropType),
    company: companyPropType,
    jobDetails: jobDetailsPropType,
    handleCompanyApplyClick: PropTypes.func,
    topApplyButtonGroupRef: refPropType,
    bottomApplyButtonGroupRef: refPropType,
    userLiked: PropTypes.bool,
    onJobSaveClick: PropTypes.func,
    hasCookie: PropTypes.bool,
    cookieBannerHeight: PropTypes.number,
    jobSearchVariant: PropTypes.bool,
    searchQueryId: PropTypes.string,
    hasCTA: PropTypes.bool,
    isJobsFindPage: PropTypes.bool,
};

SingleJobTemplateV2.defaultProps = {
    jobSearchVariant: false,
    userLiked: false,
    convincers: [],
    company: {},
    jobDetails: {},
    handleCompanyApplyClick: () => {},
    topApplyButtonGroupRef: { current: null },
    bottomApplyButtonGroupRef: { current: null },
    onJobSaveClick: null,
    hasCookie: false,
    cookieBannerHeight: 0,
    searchQueryId: null,
    hasCTA: false,
    isJobsFindPage: false,
};
